// The AI split review (pages/SplitReview): the generated workouts read as a gym sheet, before they're saved
import { estimateWorkoutMinutes, isWarmupEntry } from './workoutStats';

const WARMUP_SUFFIX = /\s*\((?:warmup|warm-up|aquecimento|feeder|preparo)\)/gi;

/** "Supino Reto com Barra (Aquecimento)" -> "Supino Reto com Barra" */
export const baseExerciseName = (name) => (name || '').replace(WARMUP_SUFFIX, '').trim();

const toNumber = (value) => {
  const number = parseFloat(value);
  return Number.isFinite(number) ? number : 0;
};

/**
 * Consecutive entries of the same movement (warm-ups, feeders, working sets) become one exercise with its sets.
 * `working` are the sets that count; `warmups` the ones before them. An exercise of only warm-ups counts them all.
 */
export const groupExercises = (exercises) => {
  const groups = [];
  (exercises || []).forEach((exercise) => {
    const name = baseExerciseName(exercise.name);
    const interval = parseInt(exercise.interval, 10);
    const set = {
      reps: toNumber(exercise.reps),
      weight: toNumber(exercise.weight),
      unit: exercise.weightUnit || 'kg',
      rest: Number.isFinite(interval) ? interval : null,
      isWarmup: isWarmupEntry(exercise),
    };
    const sets = Array.from({ length: Math.max(1, parseInt(exercise.series, 10) || 1) }, () => set);
    const last = groups[groups.length - 1];
    if (last && last.name === name) {
      last.sets.push(...sets);
      last.howTo = last.howTo || exercise.howTo || '';
      last.videoUrl = last.videoUrl || exercise.videoUrl || null;
      last.thumbnailUrl = last.thumbnailUrl || exercise.thumbnailUrl || null;
    } else {
      groups.push({
        name,
        exerciseId: exercise.exerciseId || null,
        thumbnailUrl: exercise.thumbnailUrl || null,
        videoUrl: exercise.videoUrl || null,
        howTo: exercise.howTo || '',
        sets,
      });
    }
  });
  return groups.map((group) => {
    const working = group.sets.filter((set) => !set.isWarmup);
    return working.length ? { ...group, working, warmups: group.sets.filter((set) => set.isWarmup) } : { ...group, working: group.sets, warmups: [] };
  });
};

/** One value when every set agrees ("6"), the span when they don't ("8–10"); `format` writes each number */
export const spanOf = (values, format = String) => {
  const min = Math.min(...values);
  const max = Math.max(...values);
  return min === max ? format(min) : `${format(min)}–${format(max)}`;
};

/** A workout's exercises, working sets and rough length (the dashboard's estimate) */
export const summarizeWorkout = (workout) => {
  const groups = groupExercises(workout?.exercises);
  return {
    groups,
    exerciseCount: groups.length,
    setCount: groups.reduce((total, group) => total + group.working.length, 0),
    minutes: estimateWorkoutMinutes(workout?.exercises),
  };
};

// The order a workout's muscles are listed in: upper body front to back, then arms, core and legs
const MUSCLE_ORDER = [
  'chest',
  'upper_back',
  'traps',
  'shoulders',
  'biceps',
  'triceps',
  'forearms',
  'abdominals',
  'lower_back',
  'quadriceps',
  'hamstrings',
  'glutes',
  'adductors',
  'abductors',
  'calves',
  'full_body',
  'cardio',
  'neck',
];

/**
 * The muscle groups a workout trains, from the library's muscle of each exercise. Lats and upper back read as one
 * back here (the exercise rows keep the library's own muscle). `library` maps an exerciseId to its library entry
 */
export const workoutMuscles = (groups, library) => {
  const seen = new Set(
    groups
      .map((group) => library?.[group.exerciseId]?.muscleGroup)
      .filter(Boolean)
      .map((muscle) => (muscle === 'lats' ? 'upper_back' : muscle))
  );
  return MUSCLE_ORDER.filter((muscle) => seen.has(muscle));
};

/** The letters the split takes when saved: the free ones (A–J), in order */
export const freeLetters = (letters, takenNames) => letters.filter((letter) => !(takenNames || []).includes(letter));

/** "Deite-se no banco.  \n2. Estenda os braços" -> ["Deite-se no banco.", "Estenda os braços"] */
export const howToSteps = (howTo) =>
  (howTo || '')
    .split('\n')
    .map((line) => line.replace(/^\s*\d+[.)]\s*/, '').trim())
    .filter(Boolean);
